import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useEntity } from '@backstage/plugin-catalog-react';
import { getCompoundEntityRef } from '@backstage/catalog-model';
import { Progress, ErrorPanel } from '@backstage/core-components';
import {
  Box,
  Chip,
  Paper,
  Typography,
  Theme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { parseScorecardExemptions } from './exemptions';
import { Donut } from './Donut';
import { scoreToTier, tierLabel, tierBackground } from './tiers';
import { useEntityChecks } from './hooks';
import { useCampaignOwnedCheckIds } from './useCampaignOwnedChecks';

const ALL_CHECKS = [
  'hasOwner',
  'hasDescription',
  'bitbucketDefaultBranch',
  'lifecycleValid',
  'hasSourceLocation',
  'hasWebApps',
  'hasJiraBoard',
  'bitbucketActive',
  'bitbucketNoStalePRs',
  'jiraNoCriticalBugs',
  'jiraBugBacklogManageable',
  'azureWebAppsHttpsOnly',
  'azureWebAppsManagedIdentity',
  'azureWebAppsHealthCheckPath',
  'hasTechDocs',
  'techDocsContentPresent',
  'hasTags',
  'sonarQualityGate',
  'sonarCoverage',
  'sonarDuplication',
];

const useStyles = makeStyles<Theme>(theme => ({
  card: {
    padding: theme.spacing(2),
  },
  label: {
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    fontSize: '10px',
    fontWeight: 'bold',
    letterSpacing: 0.5,
    marginBottom: theme.spacing(1.5),
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  topRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(6),
  },
  tier: {
    fontWeight: 700,
    fontSize: '1rem',
    height: 36,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    letterSpacing: 0.5,
    boxShadow:
      '0 2px 6px rgba(0,0,0,0.25), inset 0 -2px 0 rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.25)',
    border: '1px solid rgba(0,0,0,0.15)',
  },
  tierIcon: {
    marginRight: theme.spacing(0.75),
    fontSize: '1.1rem',
  },
  caption: {
    color: theme.palette.text.secondary,
    fontSize: '0.78rem',
    textAlign: 'center',
  },
}));

export const BidoneScorecardOverviewWidget: React.FC = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { entity } = useEntity();
  const exempt = parseScorecardExemptions(entity);
  const owned = useCampaignOwnedCheckIds();
  const enabled = ALL_CHECKS.filter(id => !exempt.has(id) && !owned.has(id));

  const { data, isLoading: loading, error } = useEntityChecks(
    getCompoundEntityRef(entity),
    enabled,
  );

  const passed = data?.filter(r => r.result === true).length ?? 0;
  const total = data?.length ?? 0;
  const passRate = total === 0 ? 0 : (passed / total) * 100;
  const tier = scoreToTier(passRate, total);

  return (
    <Paper
      className={classes.card}
      onClick={() => navigate('scorecards')}
      style={{ cursor: 'pointer' }}
    >
      <Typography className={classes.label}>Overall Maturity</Typography>
      {loading && <Progress />}
      {error && <ErrorPanel error={error} />}
      {!loading && !error && (
        <Box className={classes.body}>
          <Box className={classes.topRow}>
            <Donut passed={passed} total={total} size={96} />
            <Chip
              label={
                <>
                  {tier !== 'none' && (
                    <span className={classes.tierIcon}>
                      {tier === 'gold'
                        ? '\u{1F947}'
                        : tier === 'silver'
                        ? '\u{1F948}'
                        : '\u{1F949}'}
                    </span>
                  )}
                  {tierLabel[tier]}
                </>
              }
              className={classes.tier}
              style={{
                background: tierBackground[tier],
                color: '#fff',
              }}
            />
          </Box>
          <Typography className={classes.caption}>
            {total === 0
              ? 'Awaiting scoring'
              : `${passed} of ${total} checks passing`}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};
